// src/pages/DevTickets.js
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAgents } from "../api";
import { Ticket as TicketIcon, RefreshCw } from "lucide-react";
import { StatusBadge, PriorityBadge, sx } from "../theme";
import useIsMobile from "../hooks/useIsMobile";
import { HtmlContent } from "../components/HtmlContent";
import { useAuth } from "../context/AuthContext";

const API   = (process.env.REACT_APP_API_URL || "") + "/api";
const token = () => localStorage.getItem("midtc_token") || "";

const fmt     = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "—";
const fmtTime = (d) => d ? new Date(d).toLocaleString()  : "—";
const fmtMsg  = (d) => d ? new Date(d).toLocaleString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "";

// Aliases — keep the rest of the file's `{ ...cardStyle }` spreads working
const cardStyle     = sx.card;
const cardHeadStyle = { ...sx.cardHead, borderRadius: "12px 12px 0 0" };

export default function DevTickets() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { agent } = useAuth();
  const [tickets,   setTickets]   = useState([]);
  const [agents,    setAgents]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState({ status:"", priority:"", assigned_to:"" });
  const [selected,  setSelected]  = useState(null);
  const [updating,  setUpdating]  = useState(false);
  const [messages,  setMessages]  = useState([]);
  const [msgInput,  setMsgInput]  = useState("");
  const [sending,   setSending]   = useState(false);
  const messagesEndRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status)      params.append("status",      filter.status);
      if (filter.priority)    params.append("priority",    filter.priority);
      if (filter.assigned_to) params.append("assigned_to", filter.assigned_to);
      const res  = await fetch(`${API}/dev-tickets?${params}`, { headers:{ Authorization:`Bearer ${token()}` } });
      const data = await res.json();
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getAgents().then(r => setAgents(r.data || [])).catch(() => {}); }, []);

  const loadMessages = useCallback(async (devTicketId) => {
    try {
      const res  = await fetch(`${API}/dev-tickets/${devTicketId}/messages`, { headers:{ Authorization:`Bearer ${token()}` } });
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    if (selected?.id) {
      loadMessages(selected.id);
      const interval = setInterval(() => loadMessages(selected.id), 15000);
      return () => clearInterval(interval);
    } else {
      setMessages([]);
    }
  }, [selected?.id, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!msgInput.trim() || !selected) return;
    setSending(true);
    try {
      await fetch(`${API}/dev-tickets/${selected.id}/messages`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:    JSON.stringify({ body: msgInput.trim() }),
      });
      setMsgInput("");
      await loadMessages(selected.id);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const handleUpdate = async (id, updates) => {
    setUpdating(true);
    try {
      await fetch(`${API}/dev-tickets/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:    JSON.stringify(updates),
      });
      await load();
      if (selected?.id === id) setSelected(prev => ({ ...prev, ...updates }));
    } catch (err) { console.error(err); }
    finally { setUpdating(false); }
  };

  const inputStyle = {
    background: "var(--t-input)",
    border: "1px solid rgba(148,163,184,0.18)",
    borderRadius: 7,
    padding: "7px 10px",
    color: "var(--t-text-primary)",
    fontSize: 13,
    cursor: "pointer",
  };

  const cardInputStyle = {
    background: "var(--t-card-input)",
    border: "1px solid rgba(99,162,255,0.3)",
    borderRadius: 7,
    padding: "7px 10px",
    color: "var(--t-text-primary)",
    fontSize: 13,
    cursor: "pointer",
  };

  const counts = {
    open:       tickets.filter(t => t.status === "Open").length,
    inProgress: tickets.filter(t => t.status === "In Progress").length,
    resolved:   tickets.filter(t => ["Resolved","Closed"].includes(t.status)).length,
  };

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden", background:"var(--t-page)" }}>

      {/* Left — ticket list (on mobile the detail pane replaces it; × returns here) */}
      <div style={{ width: isMobile ? "100%" : (selected ? 400 : "100%"), flexShrink:0, display: isMobile && selected ? "none" : "flex", flexDirection:"column", borderRight: isMobile ? "none" : "1px solid var(--t-border-soft)", overflow:"hidden" }}>

        {/* List head */}
        <div style={{ padding:"18px 18px 13px", borderBottom:"1px solid var(--t-border-soft)", flexShrink:0,
          background:"var(--t-grad-list-head)" }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
            <div>
              <h1 style={{ margin:0, fontSize:18, fontWeight:800, color:"var(--t-text-primary)", letterSpacing:"-0.3px" }}>Dev Queue</h1>
              <p style={{ margin:"2px 0 0", color:"var(--t-text-faint)", fontSize:12 }}>{tickets.length} active dev ticket{tickets.length!==1?"s":""}</p>
            </div>
            <span style={{ marginTop:4, display:"inline-flex", alignItems:"center", gap:5,
              padding:"3px 9px", borderRadius:99, fontSize:10, fontWeight:700, whiteSpace:"nowrap", letterSpacing:"0.02em",
              background:"rgba(96,165,250,0.15)", color:"var(--badge-blue)", border:"1px solid rgba(96,165,250,0.3)" }}>
              INTERNAL
            </span>
          </div>

          {/* Mini stats */}
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            {[
              {label:"Open",       value:counts.open,       color:"#60a5fa"},
              {label:"In Progress",value:counts.inProgress, color:"#a78bfa"},
              {label:"Resolved",   value:counts.resolved,   color:"#34d399"},
            ].map(s=>(
              <div key={s.label} style={{
                flex:1, background:"var(--t-panel)", border:"1px solid rgba(148,163,184,0.14)",
                borderRadius:8, padding:"9px 11px",
                boxShadow:"inset 0 1px 0 rgba(255,255,255,0.04)",
              }}>
                <div style={{ fontSize:20, fontWeight:800, letterSpacing:"-0.5px", lineHeight:1, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:10, color:"var(--t-text-faint)", marginTop:3, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* List filters */}
        <div style={{ display:"flex", flexWrap: isMobile ? "wrap" : "nowrap", gap:6, padding: isMobile ? "9px 14px" : "9px 18px", borderBottom:"1px solid var(--t-border-soft)", flexShrink:0,
          background:"var(--t-panel)" }}>
          {[
            { key:"status",   opts:["","Open","In Progress","On Hold","Resolved","Closed"], placeholder:"All Statuses" },
            { key:"priority", opts:["","Critical","High","Medium","Low"],                    placeholder:"All Priorities" },
          ].map(f => (
            <select key={f.key} value={filter[f.key]} onChange={e => setFilter(p=>({...p,[f.key]:e.target.value}))}
              style={{ flex:1, minWidth: isMobile ? 130 : undefined, background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.14)", borderRadius:8,
                padding:"7px 10px", color:"var(--t-text-primary)", fontSize:12, fontFamily:"inherit", outline:"none", cursor:"pointer" }}>
              <option value="">{f.placeholder}</option>
              {f.opts.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          <select value={filter.assigned_to} onChange={e => setFilter(p=>({...p,assigned_to:e.target.value}))}
            style={{ flex:1, minWidth: isMobile ? 130 : undefined, background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.14)", borderRadius:8,
              padding:"7px 10px", color:"var(--t-text-primary)", fontSize:12, fontFamily:"inherit", outline:"none", cursor:"pointer" }}>
            <option value="">All Devs</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
          </select>
          <button onClick={load} title="Refresh" style={{
            padding:"7px 10px", background:"var(--t-input)", border:"1px solid rgba(148,163,184,0.14)",
            borderRadius:8, color:"var(--t-text-muted)", cursor:"pointer", display:"inline-flex", alignItems:"center", justifyContent:"center",
          }}>
            <RefreshCw size={13} />
          </button>
        </div>

        {/* Ticket list */}
        <div style={{ flex:1, overflowY:"auto" }}>
          {loading && <div style={{ padding:32, textAlign:"center", color:"var(--t-text-faint)" }}>Loading...</div>}
          {!loading && tickets.length === 0 && (
            <div style={{ padding:48, textAlign:"center", color:"var(--t-text-faint)" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🎉</div>
              <div style={{ fontSize:15, fontWeight:600, color:"var(--t-text-faint)" }}>No dev tickets</div>
              <div style={{ fontSize:13, color:"var(--t-text-faint)", marginTop:4 }}>All clear in the dev queue.</div>
            </div>
          )}
          {tickets.map(t => {
            const isSel = selected?.id === t.id;
            return (
              <div key={t.id}
                onClick={() => setSelected(isSel ? null : t)}
                style={{
                  padding:"13px 18px", borderBottom:"1px solid var(--t-border-soft)", cursor:"pointer",
                  background: isSel
                    ? "linear-gradient(90deg,rgba(124,58,237,0.12),rgba(124,58,237,0.03))"
                    : "transparent",
                  borderLeft: isSel ? "3px solid #7b61ff" : "3px solid transparent",
                  transition:"all 0.12s",
                }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "rgba(99,102,241,0.04)"; }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
              >
                {/* tr-top: title + badges */}
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8, marginBottom:6 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:"var(--t-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, minWidth:0 }}>
                    {t.title}
                  </div>
                  <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                    <PriorityBadge priority={t.priority} />
                    <StatusBadge status={t.status} />
                  </div>
                </div>

                {/* tr-meta: source #, assignee, date */}
                <div style={{ display:"flex", alignItems:"center", gap:10, fontSize:11, color:"var(--t-text-faint)", flexWrap:"wrap" }}>
                  {t.ticket_number ? (
                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, color:"#60a5fa", fontFamily:"'SF Mono','Fira Code',monospace" }}>
                      <TicketIcon size={10}/> {t.ticket_number}
                    </span>
                  ) : (
                    <span>No source ticket</span>
                  )}
                  {t.assigned_dev_details?.length > 0
                    ? <span>👤 {t.assigned_dev_details.map(d=>d.name).join(", ")}</span>
                    : <span>👤 Unassigned</span>
                  }
                  <span>Created {fmt(t.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right — ticket detail */}
      {selected && (
        <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", overflow:"hidden", background:"var(--t-page)" }}>

          {/* Detail head */}
          <div style={{ padding:"17px 20px 13px", borderBottom:"1px solid var(--t-border-soft)", flexShrink:0,
            background:"var(--t-grad-list-head)" }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:"var(--t-text-primary)", letterSpacing:"-0.3px" }}>{selected.title}</h2>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6, fontSize:12, flexWrap:"wrap" }}>
                  {selected.ticket_number && (
                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, color:"#60a5fa", fontFamily:"'SF Mono','Fira Code',monospace" }}>
                      <TicketIcon size={11}/> Source {selected.ticket_number}
                    </span>
                  )}
                  {selected.client_name && (
                    <>
                      <span style={{ color:"var(--t-text-faint)" }}>·</span>
                      <span style={{ color:"var(--t-text-muted)" }}>{selected.client_name}</span>
                    </>
                  )}
                  {selected.source_submitter_name && (
                    <>
                      <span style={{ color:"var(--t-text-faint)" }}>·</span>
                      <span style={{ color:"var(--t-text-faint)" }}>
                        Submitted by {selected.source_submitter_name}
                        {selected.source_submitter_email && ` · ${selected.source_submitter_email}`}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:"none", border:"none", color:"var(--t-text-faint)", fontSize:19, cursor:"pointer", padding:"2px 6px", lineHeight:1 }}>×</button>
            </div>
          </div>

          {/* Detail scroll */}
          <div style={{ flex:1, overflowY:"auto", padding: isMobile ? 14 : "16px 20px", display:"flex", flexDirection:"column", gap:12 }}>

          {/* Status & Assignment */}
          <div style={{ ...cardStyle }}>
            <div style={{ ...cardHeadStyle }}>Status &amp; Assignment</div>
            <div style={{ padding:14, display:"flex", gap:10, flexWrap:"wrap" }}>
              {[
                { label:"Status",   field:"status",   opts:["Open","In Progress","On Hold","Resolved","Closed"], minWidth:110 },
                { label:"Priority", field:"priority", opts:["Critical","High","Medium","Low"], minWidth:110 },
              ].map(f => (
                <div key={f.field} style={{ display:"flex", flexDirection:"column", gap:4, minWidth:f.minWidth }}>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--t-text-faint)" }}>{f.label}</div>
                  <select value={selected[f.field]} onChange={e => handleUpdate(selected.id, { [f.field]:e.target.value })}
                    style={{ ...cardInputStyle, fontSize:12, padding:"8px 11px" }} disabled={updating}>
                    {f.opts.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div style={{ ...cardStyle }}>
            <div style={{ ...cardHeadStyle }}>Description</div>
            <div style={{ padding:16, fontSize:13, color:"var(--t-text-body)", lineHeight:1.7 }}>
              {(selected.description || selected.source_description)
                ? <HtmlContent text={selected.description || selected.source_description} />
                : "No description."}
            </div>
          </div>

          {/* Assigned devs */}
          <div style={{ ...cardStyle }}>
            <div style={{ ...cardHeadStyle }}>Assigned Developers</div>
            <div style={{ padding:16, display:"flex", gap:8, flexWrap:"wrap" }}>
              {(selected.assigned_dev_details || []).map(d => (
                <div key={d.id} style={{ background:"var(--t-input-hi)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:8, padding:"6px 12px", fontSize:13 }}>
                  <div style={{ fontWeight:600, color:"var(--t-text-primary)" }}>{d.name}</div>
                  <div style={{ fontSize:11, color:"var(--t-text-faint)" }}>{d.email}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div style={{ ...cardStyle }}>
            <div style={{ ...cardHeadStyle }}>Notes</div>
            <div style={{ padding:16 }}>
              <textarea
                defaultValue={selected.notes || ""}
                onBlur={e => handleUpdate(selected.id, { notes:e.target.value })}
                rows={3}
                placeholder="Add notes..."
                style={{ background:"var(--t-card-input)", border:"1px solid rgba(99,162,255,0.3)", borderRadius:7, padding:"8px 12px", color:"var(--t-text-primary)", fontSize:13, width:"100%", resize:"vertical", boxSizing:"border-box" }}
              />
            </div>
          </div>

          {/* Dev Messages */}
          <div style={{ ...cardStyle, overflow:"hidden" }}>
            <div style={{ ...cardHeadStyle, display:"flex", alignItems:"center", gap:8, borderRadius:"12px 12px 0 0" }}>
              <span>Dev Messages</span>
              <span style={{ fontSize:11, color:"var(--t-text-faint)", fontWeight:400, textTransform:"none", letterSpacing:0 }}>— internal only, never visible to clients</span>
            </div>

            {/* Message thread */}
            <div style={{ maxHeight:340, overflowY:"auto", padding:"12px 16px", display:"flex", flexDirection:"column", gap:10 }}>
              {messages.length === 0 && (
                <div style={{ textAlign:"center", color:"var(--t-text-faint)", fontSize:13, padding:"20px 0" }}>No messages yet. Replies to the dev ticket email will appear here.</div>
              )}
              {messages.map(m => {
                const isMine = !!agent?.id && m.agent_id === agent.id;
                const name   = m.agent_name_resolved || m.agent_name || "Unknown";
                return (
                  <div key={m.id} style={{ display:"flex", flexDirection: isMine ? "row-reverse" : "row", alignItems:"flex-end", gap:8 }}>
                    {/* Avatar — only on the other person's side */}
                    {!isMine && (
                      <span style={{ width:28, height:28, borderRadius:"50%", background:"rgba(99,102,241,0.2)", color:"var(--badge-violet)", fontSize:11, fontWeight:700, display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {name.charAt(0).toUpperCase()}
                      </span>
                    )}

                    {/* Bubble + meta */}
                    <div style={{ display:"flex", flexDirection:"column", alignItems: isMine ? "flex-end" : "flex-start", maxWidth:"78%", minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3, flexDirection: isMine ? "row-reverse" : "row" }}>
                        {!isMine && <span style={{ fontSize:12, fontWeight:600, color:"var(--badge-violet)" }}>{name}</span>}
                        <span style={{ fontSize:10, color:"var(--t-text-faint)" }}>{fmtMsg(m.created_at)}</span>
                        {m.source === "email" && (
                          <span style={{ fontSize:9, background:"rgba(148,163,184,0.1)", color:"var(--t-text-faint)", border:"1px solid rgba(148,163,184,0.18)", borderRadius:4, padding:"1px 5px" }}>via email</span>
                        )}
                      </div>
                      <div style={{
                        background: isMine ? "linear-gradient(135deg,#4f6fff,#7b61ff)" : "var(--t-input-hi)",
                        border: isMine ? "none" : "1px solid rgba(148,163,184,0.18)",
                        borderRadius: isMine ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                        padding:"8px 12px", fontSize:13, lineHeight:1.6, wordBreak:"break-word",
                        color: isMine ? "#fff" : "var(--t-text-body)",
                        boxShadow: isMine ? "0 2px 10px rgba(99,102,241,0.25)" : "none",
                      }}>
                        <HtmlContent text={m.body} />
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Compose */}
            <div style={{ padding:"10px 16px", borderTop:"1px solid var(--t-border-soft)", display:"flex", gap:8, alignItems:"flex-end" }}>
              <textarea
                value={msgInput}
                onChange={e => setMsgInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Type a dev message… (Enter to send, Shift+Enter for new line)"
                rows={2}
                style={{ flex:1, background:"var(--t-card-input)", border:"1px solid rgba(99,162,255,0.3)", borderRadius:8, padding:"8px 12px", color:"var(--t-text-primary)", fontSize:13, resize:"none", boxSizing:"border-box" }}
              />
              <button
                onClick={sendMessage}
                disabled={sending || !msgInput.trim()}
                style={{
                  background: "linear-gradient(135deg,#4f6fff,#7b61ff)", color:"#fff",
                  border:"none", borderRadius:8,
                  boxShadow: sending || !msgInput.trim() ? "none" : "0 4px 16px rgba(99,102,241,0.4)",
                  padding:"8px 16px", fontWeight:600, fontSize:13,
                  cursor: sending || !msgInput.trim() ? "not-allowed" : "pointer",
                  opacity: sending || !msgInput.trim() ? 0.5 : 1, flexShrink:0,
                }}
              >
                {sending ? "…" : "Send"}
              </button>
            </div>
          </div>

          {/* Source ticket link */}
          {selected.source_ticket_id && (
            <button
              onClick={() => navigate(`/tickets/${selected.source_ticket_id}`)}
              style={{ background:"rgba(99,102,241,0.1)", color:"#818cf8", border:"1px solid rgba(99,102,241,0.3)", padding:"10px 16px", borderRadius:8, fontWeight:600, fontSize:13, cursor:"pointer", textAlign:"left" }}
            >
              🎫 View Source Ticket: {selected.ticket_number} — {selected.source_subject}
            </button>
          )}

          <div style={{ fontSize:12, color:"var(--t-text-faint)" }}>
            Created {fmtTime(selected.created_at)} · Last updated {fmtTime(selected.updated_at)}
          </div>

          </div>{/* /detail-scroll */}
        </div>
      )}
    </div>
  );
}
